/**
 * @fileoverview 5-year (or single-year monthly) trend area chart.
 * @description Ported near-verbatim from the InsightBox Banking design system's own
 * `charts/TrendArea.tsx` — the label-collision-avoidance geometry (row-flipping two value labels
 * that land too close together, hiding a growth-% sub-label that would collide with a neighbor) is
 * a real Recharts-crash workaround discovered by measurement, not incidental styling, so it's kept
 * intact including the original explanatory comments. Only the color-constant imports (this app's
 * own `chart-theme.constants.ts` instead of the reference's `charts/colors.ts` — `CHART_BRAND`
 * there is numerically identical to `CHART_PRIMARY` here, not `CHART_ACCENT`, which corresponds to
 * the reference's separate `CHART_NAVY`) and the two render-prop functions' typing (recharts' own
 * exported `DotItemDotProps`/`LabelProps` instead of the reference's untyped `any`, since this
 * app's lint config forbids `no-explicit-any`) were adapted; feature-scoped rather than promoted to
 * `shared/` since its data shape (`TrendPoint`) is banking-specific.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useLayoutEffect, useRef, useState } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  CHART_ERROR,
  CHART_GRID,
  CHART_PRIMARY,
  CHART_SUCCESS,
  CHART_WARNING,
} from '@/shared/constants/chart-theme.constants'

import type { DotItemDotProps, LabelProps } from 'recharts'

export interface TrendPoint {
  fy: string
  value: number
  growthPct: number | null
}

/** Rise into a point = green, fall into a point = red. The first point has
 * no prior value, so it borrows the direction of the segment leaving it. */
function pointColor(data: TrendPoint[], index: number): string {
  if (index === 0) {
    return data.length > 1 && data[1]!.value < data[0]!.value ? CHART_ERROR : CHART_SUCCESS
  }
  return data[index]!.value >= data[index - 1]!.value ? CHART_SUCCESS : CHART_ERROR
}

function makeValueDot(data: TrendPoint[]) {
  return function ValueDot(props: DotItemDotProps) {
    const { cx, cy, index } = props
    if (typeof cx !== 'number' || typeof cy !== 'number') return null
    return (
      <circle cx={cx} cy={cy} r={5} fill={pointColor(data, index)} stroke="#fff" strokeWidth={2} />
    )
  }
}

/** Smooth gradient: one stop per point (green if it rose from the previous
 * point, red if it fell), blending gradually between them. Where two
 * adjacent points have different colors, an amber stop is inserted at their
 * midpoint so the transition reads as a soft fade (green -> amber -> red)
 * instead of a muddy direct green-to-red mix. */
function buildSegmentStops(data: TrendPoint[]): { offset: number; color: string }[] {
  const stops: { offset: number; color: string }[] = []
  data.forEach((_, i) => {
    const offset = (i / (data.length - 1)) * 100
    stops.push({ offset, color: pointColor(data, i) })
    if (i < data.length - 1) {
      const nextOffset = ((i + 1) / (data.length - 1)) * 100
      if (pointColor(data, i) !== pointColor(data, i + 1)) {
        stops.push({ offset: (offset + nextOffset) / 2, color: CHART_WARNING })
      }
    }
  })
  return stops
}

/** Row-to-row vertical offset for one point's value+growth label block in
 * dense mode — the two lines within a single point's own label are kept
 * tight (see growthDy) so this gap only has to clear one compact 2-line
 * block, not two loosely-spaced lines. */
const DENSE_ROW_GAP = 34

function makeValueLabel(
  data: TrendPoint[],
  formatValue: (v: number) => string,
  dense: boolean,
  hiddenGrowth: Set<number>,
  rowFlips: Set<number>,
) {
  return function ValueLabel(props: LabelProps) {
    const { x, y, index } = props
    const point = index === undefined ? undefined : data[index]
    if (!point || index === undefined || typeof x !== 'number' || typeof y !== 'number') return null
    const growth = point.growthPct
    const anchor = index === 0 ? 'start' : index === data.length - 1 ? 'end' : 'middle'
    // The first point's label starts exactly at the point (no leftward
    // nudge) — pulling it further left, toward the Y-axis's own tick
    // labels, was crowding/overlapping them on a narrow chart where the
    // first point already sits close to the axis.
    const dx = index === data.length - 1 ? 4 : 0
    // Every point keeps its value label. A dense (e.g. 12-point monthly)
    // view packs them too close for one shared row, so labels alternate
    // onto a second row by index parity — a fixed, always-legible zigzag
    // rather than a data-driven placement that can itself misjudge how
    // close two points really are. Two points sharing a row are 2 indices
    // apart, so a near-flat run in the data can still land them close
    // enough to collide even with the stagger — rowFlips corrects those
    // specific points (found by real measurement, see the effect below) by
    // moving them to the other row. Whether the smaller secondary growth-%
    // line also fits is decided the same way, from measured geometry
    // rather than guessed here.
    const row = rowFlips.has(index) ? 1 - (index % 2) : index % 2
    const stagger = dense && row === 1 ? DENSE_ROW_GAP : 0
    const valueSize = dense ? 'text-[12px]' : 'text-[13px]'
    const growthSize = dense ? 'text-[9px]' : 'text-[11px]'
    const growthDy = 14
    const showGrowth = growth !== null && growth !== undefined && !hiddenGrowth.has(index)
    return (
      <g>
        <text
          x={x + dx}
          y={y - 22 - stagger}
          textAnchor={anchor}
          data-label-role="value"
          data-label-index={index}
          className={`fill-text font-semibold ${valueSize}`}
        >
          {formatValue(point.value)}
        </text>
        {showGrowth && (
          <text
            x={x + dx}
            y={y - 22 - stagger + growthDy}
            textAnchor={anchor}
            data-label-role="growth"
            data-label-index={index}
            className={`font-medium ${growthSize}`}
            fill={growth >= 0 ? CHART_SUCCESS : CHART_ERROR}
          >
            {growth >= 0 ? '+' : ''}
            {growth.toFixed(2)}%
          </text>
        )}
      </g>
    )
  }
}

/** Evenly-spaced, round-number tick values spanning at least
 * [rawMin, rawMax]. Recharts' own default tick generation keeps the exact
 * (often oddly-fractional, padding-derived) domain boundary as a tick,
 * which can make the outermost gap on the axis visibly smaller than every
 * other gap — rounding the bounds themselves to a clean step first, then
 * generating every tick directly from that step, keeps every gap equal. */
function niceAxisTicks(rawMin: number, rawMax: number, targetCount = 5): number[] {
  const range = Math.max(rawMax - rawMin, 1e-9)
  const roughStep = range / (targetCount - 1)
  const magnitude = 10 ** Math.floor(Math.log10(roughStep))
  const normalized = roughStep / magnitude
  const niceNormalized = normalized < 1.5 ? 1 : normalized < 3 ? 2 : normalized < 7 ? 5 : 10
  const step = niceNormalized * magnitude
  const niceMin = Math.floor(rawMin / step) * step
  const ticks: number[] = []
  for (let t = niceMin; t < rawMax + step / 2; t += step) {
    ticks.push(Math.round(t * 1000) / 1000)
  }
  return ticks
}

function roundNice(value: number, max: number): string {
  if (max >= 100) return Math.round(value).toLocaleString('en-IN')
  if (max >= 10) return value.toFixed(1)
  return value.toFixed(2)
}

interface TrendAreaProps {
  data: TrendPoint[]
  formatValue: (v: number) => string
  height?: number | '100%'
  /** Color the fill and dots by direction (green rising, red falling, amber
   * at direction changes) instead of a flat brand-color wash — matching the
   * Member Count trend chart's styling. */
  colorByTrend?: boolean
}

/**
 * @component
 */
function TrendArea({ data, formatValue, height = 320, colorByTrend = false }: TrendAreaProps) {
  const max = Math.max(...data.map((d) => d.value))
  const min = Math.min(...data.map((d) => d.value))
  const dense = data.length > 8
  const rawAxisMin = Math.max(0, min - (max - min) * 0.5 - max * 0.05)
  const rawAxisMax = max + (max - min) * 0.45 + max * 0.05
  const axisTicks = niceAxisTicks(rawAxisMin, rawAxisMax)
  const marginTop = dense ? 76 : 48

  const wrapRef = useRef<HTMLDivElement>(null)
  const [hiddenGrowth, setHiddenGrowth] = useState<Set<number>>(new Set())
  const [rowFlips, setRowFlips] = useState<Set<number>>(new Set())
  // Resetting these two during render (React's documented "adjusting state when a prop changes"
  // pattern) rather than as the first thing a useLayoutEffect below does keeps that effect's body
  // free of a synchronous setState call — the effect itself is still needed (and still does call
  // setState) for the actual DOM-measurement-driven updates, but those happen asynchronously
  // inside a ResizeObserver/timeout callback, not synchronously in the effect body.
  const [wasDense, setWasDense] = useState(dense)
  if (dense !== wasDense) {
    setWasDense(dense)
    if (!dense) {
      setHiddenGrowth(new Set())
      setRowFlips(new Set())
    }
  }
  // Persists across the effect's re-runs (one per flip, since each flip
  // changes `rowFlips`, a dependency) — a plain local variable would reset
  // to 0 on every one of those re-runs and never actually bound anything.
  // Reset only when `data` itself genuinely changes (see the effect below),
  // not on every rowFlips-triggered re-run, which is exactly what this is
  // meant to count.
  const flipRoundsRef = useRef(0)
  const flipRoundsDataRef = useRef<TrendPoint[] | null>(null)

  // Deciding which growth-% sub-labels to hide, and which points need their
  // row flipped, from actual rendered geometry (rather than an estimated
  // formula) avoids the calibration drift a plot-height/domain estimate is
  // prone to in fill mode, where the real pixel height isn't known upfront.
  // Row flips are resolved first: a flip changes a value label's Y
  // position, so growth-hiding (which only ever hides, never repositions)
  // is decided on the settled, post-flip layout — otherwise it could hide
  // a growth label to avoid a collision that a pending flip was about to
  // remove anyway. Once positions are stable, hiding a box can't create a
  // new overlap, so growth-hiding itself is a single idempotent pass, with
  // no risk of the cascading push-loop a measure-and-shift approach hit
  // earlier.
  useLayoutEffect(() => {
    if (flipRoundsDataRef.current !== data) {
      flipRoundsRef.current = 0
      flipRoundsDataRef.current = data
    }
    if (!dense) return
    let timeout: number | undefined
    const MAX_FLIP_ROUNDS = 5
    // ResponsiveContainer's own debounce={200} (added for the separate,
    // confirmed resize-flicker fix) delays re-rendering its children on
    // ANY prop change it's involved in, not just container resizes — so
    // right after `data` changes (e.g. switching the FY dropdown), the SVG
    // can still hold the PREVIOUS dataset's label elements for up to ~200ms.
    // Retrying until the rendered value-label count actually matches the
    // current data avoids measuring that stale, about-to-be-replaced DOM.
    const measure = (attempt = 0) => {
      const svg = wrapRef.current?.querySelector('svg.recharts-surface')
      const valueBoxes = new Map<number, DOMRect>()
      const growthBoxes = new Map<number, DOMRect>()
      if (svg) {
        svg.querySelectorAll('[data-label-role="value"]').forEach((el) => {
          const idx = Number((el as SVGGraphicsElement).dataset['labelIndex'])
          valueBoxes.set(idx, (el as SVGGraphicsElement).getBBox())
        })
      }
      if (!svg || (valueBoxes.size !== data.length && attempt < 10)) {
        timeout = window.setTimeout(() => measure(attempt + 1), 50)
        return
      }
      const overlaps = (a: DOMRect, b: DOMRect) =>
        a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height

      // Value labels are never hidden (every point's value must stay
      // visible), so a value-vs-value collision can only be resolved by
      // moving one of the pair to the other row. This mainly hits points 2
      // indices apart (same row by parity) during a near-flat run in the
      // data, where the natural vertical gap is too small for even the
      // row stagger to clear. Flipping is capped at a few rounds as a hard
      // backstop against re-litigating the same pair forever.
      const flippedValueIdx = [...valueBoxes.keys()].sort((a, b) => a - b)
      const nextFlips = new Set(rowFlips)
      const rowOf = (idx: number) => (nextFlips.has(idx) ? 1 - (idx % 2) : idx % 2)
      // A flip moves a label vertically by one row-gap; approximating its
      // new box from the CURRENT measured one (rather than waiting a whole
      // extra render to find out) is what lets a flip be checked against
      // its immediate neighbors before committing to it — otherwise moving
      // a point 2 indices away into row 1 can silently land it on top of
      // its 1-apart neighbor already there (which two points sharing a row
      // will always do, since they're too close horizontally to share one
      // regardless of vertical spacing).
      const shiftedBox = (idx: number): DOMRect => {
        const box = valueBoxes.get(idx)!
        const dy = rowOf(idx) === 1 ? DENSE_ROW_GAP : -DENSE_ROW_GAP
        return { x: box.x, y: box.y + dy, width: box.width, height: box.height } as DOMRect
      }
      const safeToFlip = (idx: number) =>
        [idx - 1, idx + 1].every(
          (n) => !valueBoxes.has(n) || !overlaps(shiftedBox(idx), valueBoxes.get(n)!),
        )
      let flipChanged = false
      if (flipRoundsRef.current < MAX_FLIP_ROUNDS) {
        for (let a = 0; a < flippedValueIdx.length; a++) {
          for (let b = a + 1; b < flippedValueIdx.length; b++) {
            const i = flippedValueIdx[a]!
            const j = flippedValueIdx[b]!
            if (!overlaps(valueBoxes.get(i)!, valueBoxes.get(j)!)) continue
            if (rowOf(i) !== rowOf(j)) continue // different rows already separate them vertically
            if (safeToFlip(j)) {
              if (nextFlips.has(j)) nextFlips.delete(j)
              else nextFlips.add(j)
              flipChanged = true
            } else if (safeToFlip(i)) {
              if (nextFlips.has(i)) nextFlips.delete(i)
              else nextFlips.add(i)
              flipChanged = true
            }
            // Otherwise neither side of this pair can move without
            // colliding with its own immediate neighbor — leave the
            // (rare) residual overlap rather than thrash indefinitely.
          }
        }
      }
      if (flipChanged) {
        flipRoundsRef.current += 1
        setRowFlips(nextFlips)
        return // re-measure on the next render, once the flip takes effect
      }

      svg.querySelectorAll('[data-label-role="growth"]').forEach((el) => {
        const idx = Number((el as SVGGraphicsElement).dataset['labelIndex'])
        growthBoxes.set(idx, (el as SVGGraphicsElement).getBBox())
      })
      const next = new Set<number>()
      const sortedIdx = [...growthBoxes.keys()].sort((a, b) => a - b)
      // Two points sharing a row (same stagger tier) are 2 indices apart,
      // not 1 — checking only immediate neighbors missed exactly that case
      // (e.g. a growth label at index 2 colliding with index 0's value
      // label). Checking against every value box, and every growth box
      // already decided visible earlier in this same left-to-right pass,
      // catches it regardless of how far apart the colliding indices are.
      for (const idx of sortedIdx) {
        const box = growthBoxes.get(idx)!
        let collide = false
        for (const [vIdx, vBox] of valueBoxes) {
          if (vIdx === idx) continue
          if (overlaps(box, vBox)) {
            collide = true
            break
          }
        }
        if (!collide) {
          for (const kIdx of sortedIdx) {
            if (kIdx >= idx) break
            if (next.has(kIdx)) continue
            if (overlaps(box, growthBoxes.get(kIdx)!)) {
              collide = true
              break
            }
          }
        }
        if (collide) next.add(idx)
      }
      setHiddenGrowth((prev) => {
        if (prev.size === next.size && [...prev].every((v) => next.has(v))) return prev
        return next
      })
    }
    const debounced = () => {
      window.clearTimeout(timeout)
      // Longer than ResponsiveContainer's own debounce={200} — on a pure
      // resize, or right after a row flip, the label *count* is unchanged
      // (so the retry-until-count-matches guard above can't tell "stale"
      // from "final"), only the positions move. Waiting past recharts' own
      // debounce avoids measuring a still-settling layout. The initial run
      // goes through this same delay for the same reason: a row flip
      // re-renders with a new (same-length) label set, which is exactly
      // the case that guard can't detect on its own.
      timeout = window.setTimeout(() => measure(), 260)
    }
    debounced()
    const ro = new ResizeObserver(debounced)
    if (wrapRef.current) ro.observe(wrapRef.current)
    return () => {
      window.clearTimeout(timeout)
      ro.disconnect()
    }
    // rowFlips is a dependency so a flip (which changes label Y positions)
    // triggers exactly one more measurement pass against the settled,
    // post-flip DOM — growth-hiding is only ever decided once positions
    // have stopped moving.
  }, [dense, data, rowFlips])

  const ValueLabel = makeValueLabel(data, formatValue, dense, hiddenGrowth, rowFlips)
  const ValueDot = colorByTrend ? makeValueDot(data) : undefined
  const segmentStops = colorByTrend ? buildSegmentStops(data) : null

  return (
    <div
      ref={wrapRef}
      className={height === '100%' ? 'absolute inset-0' : undefined}
      style={height === '100%' ? undefined : { width: '100%', height }}
    >
      {/* In fill mode (height="100%"), this wrapper div's own absolute+inset-0
          gives ResponsiveContainer a definite-height ancestor to resolve its
          percentage height against — a plain CSS percentage height doesn't
          reliably get that from a flex-grow-sized parent otherwise
          (confirmed: it silently collapses to 0 in this app's layout
          chain). No-op for a fixed numeric height, which sizes normally in
          flow either way. */}
      <ResponsiveContainer width="100%" height={height} debounce={200}>
        {/* Extra top margin on a dense (e.g. monthly) view makes room for
            the second staggered label row — without it, a staggered label
            on an already-near-the-top point gets clipped. */}
        <AreaChart data={data} margin={{ top: marginTop, right: 36, left: 8, bottom: 8 }}>
          <defs>
            {colorByTrend && segmentStops ? (
              <linearGradient id="trendFill" x1="0" y1="0" x2="1" y2="0">
                {segmentStops.map((s, i) => (
                  <stop key={i} offset={`${s.offset}%`} stopColor={s.color} stopOpacity={0.4} />
                ))}
              </linearGradient>
            ) : (
              <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={CHART_PRIMARY} stopOpacity={0.22} />
                <stop offset="100%" stopColor={CHART_PRIMARY} stopOpacity={0.02} />
              </linearGradient>
            )}
          </defs>
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <XAxis
            dataKey="fy"
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#111827', fontSize: 13, fontWeight: 600 }}
          />
          <YAxis
            domain={[axisTicks[0]!, axisTicks[axisTicks.length - 1]!]}
            ticks={axisTicks}
            tickFormatter={(v: number) => roundNice(v, max)}
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#6B7280', fontSize: 11 }}
            width={48}
          />
          <Tooltip
            formatter={(v) => formatValue(Number(v))}
            contentStyle={{ borderRadius: 12, borderColor: '#E5E7EB' }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={CHART_PRIMARY}
            strokeWidth={2.5}
            fill="url(#trendFill)"
            dot={ValueDot ?? { r: 5, fill: CHART_PRIMARY, strokeWidth: 2, stroke: '#fff' }}
            activeDot={{ r: 6 }}
            label={ValueLabel}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

export default TrendArea
